/**
 * @author: ALex Kushner <alex@hackcapital.com>
 * @date:   2018-11-09 16:28:22
 * @lastModifiedBy:   Nico Alimin <nico@hackcapital.com>
 * @lastModifiedTime: 2018-11-21 18:12:18
 *
 * Recurrence date picker logic
 *
 * @copyright (c) 2018 Hack Capital
 */
import dateFns from 'date-fns';
import Preferences from '../config/recurrence';

const preferences = Preferences;

const MonthlyRecurrence = {
  DayOfMonth: 1,
  WeekOfMonth: 2,
};

const RecurrenceEndType = {
  Forever: 0,
  Until: 1,
  Count: 2,
};

const DefaultCounts = {
  DAILY: 30,
  WEEKLY: 13,
  MONTHLY: 12,
  YEARLY: 5,
};

const FrequencyIntervals = {
  DAILY: 'days',
  WEEKLY: 'weeks',
  MONTHLY: 'months',
  YEARLY: 'years',
};

const WeekdayOrdinals = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
};

// Indexes the days of the week and returns it's correponding day name
// Required because DateFns doesn't support this function built-in, unlike moment
const daysIndex = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

export default class Recurrence {
  static get DAILY() {
    return preferences.FREQUENCY.DAILY;
  }

  static get WEEKLY() {
    return preferences.FREQUENCY.WEEKLY;
  }

  static get MONTHLY() {
    return preferences.FREQUENCY.MONTHLY;
  }

  static get YEARLY() {
    return preferences.FREQUENCY.YEARLY;
  }

  static get DEFAULT_COUNTS() {
    return DefaultCounts;
  }

  constructor(initialDate, timeZone = preferences.DEFAULT_TIMEZONE, initialRule = 'FREQ=DAILY;INTERVAL=1', isCampaignSetup = false) {
    const rule = this._parseRule(initialRule);
    // TODO NA: Find a viable alternative to convert a time object to a desired time zone.
    // this.initialDate = moment.tz(initialDate, timeZone);
    this.initialDate = initialDate;
    this.frequency = rule.FREQ || preferences.FREQUENCY.DAILY;
    this.monthlyRepeatType = this._parseDefaultMonthlyRepeatType(rule.BYDAY, rule.BYSETPOS);
    this.interval = parseInt(rule.INTERVAL || 1, 10);
    this.endType = this._parseDefaultEndType(rule.COUNT, rule.UNTIL);
    this.count = parseInt(rule.COUNT || DefaultCounts[this.frequency], 10);
    this.isCampaignSetup = isCampaignSetup

    const defaultUntilDay = dateFns.addDays(this.initialDate, 2)
    this.until = (rule.UNTIL) ? dateFns.format(rule.UNTIL, 'YYYY-MM-DD') : this._parseDefaultUntil(defaultUntilDay, this.frequency);
    this.byday = rule.BYDAY || this._parseDefaultByDay(this.initialDate);
    this.daysOfMonth = 1;
  }

  get frequencies() {
    const plural = this.interval === 1 ? '' : 's';
    if (this.isCampaignSetup) {
      return [
        { value: preferences.FREQUENCY.DAILY, name: `Day${plural}` },
        { value: preferences.FREQUENCY.WEEKLY, name: `Week${plural}` },
        { value: preferences.FREQUENCY.YEARLY, name: `Year${plural}` },
      ];
    }
    return [
      { value: preferences.FREQUENCY.DAILY, name: `Day${plural}` },
      { value: preferences.FREQUENCY.WEEKLY, name: `Week${plural}` },
      { value: preferences.FREQUENCY.MONTHLY, name: `Month${plural}` },
      { value: preferences.FREQUENCY.YEARLY, name: `Year${plural}` },
    ];
  }

  get weekday() {
    return {
      id: dateFns.format(this.initialDate, 'dd').toUpperCase(),
      name: dateFns.format(this.initialDate, 'dddd'),
    };
  }

  get weekNumber() {
    // week number
    const weekNumber = dateFns.differenceInCalendarWeeks(
      this.initialDate, dateFns.startOfMonth(this.initialDate),
    ) + 1;
    return weekNumber < 5 ? weekNumber : -1; // last week of month is coded as '-1' per RFC5545
  }

  get weekName() {
    switch (this.weekNumber) {
      case 1:
        return 'first';
      case 2:
        return 'second';
      case 3:
        return 'third';
      case 4:
        return 'fourth';
      case -1:
        return 'last';
      default:
        return 'unknown';
    }
  }

  get dayOfMonth() {
    return parseInt(dateFns.format(this.initialDate, 'D'), 10);
  }

  get monthlyRepeatTypes() {
    return [
      {
        key: 1,
        day: this.dayOfMonth,
        description: `Monthly on day ${this.dayOfMonth}`,
      },
      {
        key: 2,
        week: this.weekNumber,
        weekday: this.weekday.id,
        description: `Monthly on ${this.weekName} ${this.weekday.name}`,
      },
    ];
  }

  get monthlyRepeat() {
    return this.monthlyRepeatTypes.find(v => v.key === this.monthlyRepeatType);
  }

  /*
   * @param {array} array The array of two-letter-days
   * @returns {array} The corresponding number of the week of the given days.
   *
   * Given a list of days, return back the list of days in their index of the week
   * e.g. ["Monday", "Th", "Sun"] => [1, 4, 0]
   */
  _convertToIndex(array) {
    return array.map(day => {
      return WeekdayOrdinals[day.toUpperCase().substring(0, 2)];
    });
  }
  /*
   * @param {String} desiredDay The desired day we want to start counting the offset from
   * @param {Array} listOfDays The potential days we want to search the closest offset from
   * @returns {Number} The number of days offset that is the closest from our desired day.
   *
   * Given a desired day, and a list of potential days, find how many days are to the earliest
   * day on the list.
   * e.g. Desired day = "Wednesday", list of days = "Monday, Tuesday". Returns "5"
   * Because Monday is the next closest day, and there are 5 days from Wednesday until Monday.
   */
  _findNextDayOffset(desiredDay, listOfDays) {
    const desiredDayIndex = this._convertToIndex([desiredDay])[0];
    const listOfDaysIndex = this._convertToIndex(listOfDays);
    const offsettedDays = listOfDaysIndex.map(day => {
      return (day < desiredDayIndex) ? day + 7 - desiredDayIndex : day - desiredDayIndex;
    });
    return Math.min(...offsettedDays);
  }

  /*
   * @returns {Date} The actual start date given the current day and offset/
   *
   * When weekly recurring is selected, returns the actual start date to be scheduled
   */
  get actualStartDate() {
    const currentDay = dateFns.format(this.initialDate, 'dd');
    const dayOffset = this._findNextDayOffset(currentDay, this.byday);
    return dateFns.addDays(this.initialDate, dayOffset);
  }

  get actualEndDate() {
    if (this.endType === RecurrenceEndType.Forever) return null;
    if (this.endType === RecurrenceEndType.On) return dateFns.format(this.until, 'YYYY-MM-DD');
    return this.initialDate
      .add(this.count * this.interval, FrequencyIntervals[this.frequency])
      .toISOString();
  }

  _parseRule(rule) {
    const arrayProps = ['BYDAY']; // properties always treated as an array
    const result = {};
    rule.split(';').forEach(rulePart => {
      const [key, val] = rulePart.split('=');
      if (val) {
        if (arrayProps.indexOf(key) >= 0) result[key] = val.split(',');
        else result[key] = val;
      }
    });
    return result;
  }

  get bydayAsText() {
    const days = this.byday.map(d => WeekdayOrdinals[d]).sort();
    const dayNames = days.map(d => {
      return daysIndex[d];
    }).filter(e => !!e);
    if (dayNames.length < 3) {
      return dayNames.join(' and ');
    }
    if (days.length === 5 && days.join('') === '12345') {
      return 'Monday to Friday';
    }
    dayNames[dayNames.length - 1] = `and ${dayNames[dayNames.length - 1]}`;
    return dayNames.join(', ');
  }

  _parseDefaultMonthlyRepeatType(byDay, bySetPos) {
    if (byDay && bySetPos) return MonthlyRecurrence.WeekOfMonth;
    return MonthlyRecurrence.DayOfMonth;
  }

  _parseDefaultByDay(date) {
    if (date) {
      return [
        dateFns
          .format(date, 'dd')
          .toUpperCase(),
      ];
    }
    return [];
  }

  _parseDefaultEndType(count, until) {
    if (count) return RecurrenceEndType.Count;
    if (until) return RecurrenceEndType.Until;
    return RecurrenceEndType.Forever;
  }

  _parseDefaultUntil(date, frequency) {
    let tempDate = date;
    switch (frequency) {
      case FrequencyIntervals.DAILY: {
        tempDate = dateFns.addDays(tempDate, 'days');
        break;
      }
      case FrequencyIntervals.WEEKLY: {
        tempDate = dateFns.addWeeks(tempDate, 'weeks');
        break;
      }
      case FrequencyIntervals.MONTHLY: {
        tempDate = dateFns.addMonths(tempDate, 'months');
        break;
      }
      case FrequencyIntervals.YEARLY: {
        tempDate = dateFns.addYears(tempDate, 'years');
        break;
      }
      default: break;
    }
    return dateFns.format(tempDate, preferences.DEFAULT_DATE_FORMAT);
  }

  toRecurrenceRule() {
    const rules = [`FREQ=${this.frequency}`, `INTERVAL=${this.interval}`];
    if (this.endType === RecurrenceEndType.Until && this.until) {
      rules.push(`UNTIL=${dateFns.format(this.until, 'YYYYMMDD')}`);
    } else if (this.endType === RecurrenceEndType.Count) {
      rules.push(`COUNT=${this.count}`);
    }
    if (this.frequency === preferences.FREQUENCY.WEEKLY && this.byday.length > 0) {
      const vals = this.byday.filter(d => d !== "INVALID DATE")
      rules.push(`BYDAY=${vals.join(',')}`);
    }
    if (this.frequency === preferences.FREQUENCY.MONTHLY) {
      const thisRepeat = this.monthlyRepeat;
      if (this.monthlyRepeatType === MonthlyRecurrence.DayOfMonth) {
        rules.push(`BYMONTHDAY=${thisRepeat.day}`);
      } else if (this.monthlyRepeatType === MonthlyRecurrence.WeekOfMonth) {
        rules.push(`BYSETPOS=${thisRepeat.week}`);
        rules.push(`BYDAY=${thisRepeat.weekday}`);
      }
    }
    return rules.join(';');
  }

  toDisplayString() {
    if (this.endType === RecurrenceEndType.Count && this.count === 1) {
      return 'Once';
    }
    let displayString;
    switch (this.frequency) {
      case preferences.FREQUENCY.DAILY: {
        if (parseInt(this.interval, 10) === 1) {
          displayString = 'Daily';
        } else {
          displayString = `Every ${this.interval} days`;
        }
        break;
      }
      case preferences.FREQUENCY.WEEKLY: {
        const days = this.bydayAsText;
        if (!days) {
          displayString = 'Weekly';
          break
        }
        if (parseInt(this.interval, 10) === 1) {
          displayString = `Weekly on ${days}`;
        } else {
          displayString = `Every ${this.interval} weeks on ${days}`;
        }
        break;
      }
      case preferences.FREQUENCY.MONTHLY: {
        if (this.isCampaignSetup) {
          displayString = 'Monthly'
          break
        }
        if (this.monthlyRepeatType === MonthlyRecurrence.DayOfMonth) {
          if (parseInt(this.interval, 10) === 1) {
            displayString = `Monthly on day ${this.dayOfMonth}`;
          } else {
            displayString = `Every ${this.interval} months on day ${this.dayOfMonth}`;
          }
        } else if (parseInt(this.interval, 10) === 1) {
          displayString = `Monthly on ${this.weekName} ${this.weekday.name}`;
        } else {
          displayString = `Every ${this.interval} months on ${this.weekName} ${this.weekday.name}`;
        }
        break;
      }
      case preferences.FREQUENCY.YEARLY: {
        if (this.isCampaignSetup) {
          displayString = 'Annually'
          break
        }
        if (parseInt(this.interval, 10) === 1) {
          displayString = `Annually on ${dateFns.format(this.initialDate, 'MMMM Do')}`;
        } else {
          displayString = `Every ${this.interval} years on ${dateFns.format(this.initialDate, 'MMMM Do')}`;
        }
        break;
      }
      default:
    }
    if (this.endType === RecurrenceEndType.Until) {
      return `${displayString}, until ${dateFns.format(this.until, 'MMM D, YYYY')}`;
    } else if (this.endType === RecurrenceEndType.Count) {
      return displayString;
    }
    return displayString;
  }
}
